import React, { useMemo, useState } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  X,
  Download,
  Loader2,
  Users,
  Clock,
  CircleCheck,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import { getJudgementDetail } from "../../services/ngtApi";
import { STATUS_PILL_BASE, statusPillSoft } from "../../styles/statusPill";

const ROWS_PER_PAGE = 15;

// The NGT detail page is keyed by zonal bench + filing number. The bench code
// is the suffix on the case number (e.g. "... / DELHI"); the filing number is
// the row's diary number.
const extractZone = (caseNumber = "") => {
  const parts = String(caseNumber).split("/");
  return (parts[parts.length - 1] || "").trim();
};

const SEARCH_TYPE_LABELS = {
  date: "Date Range",
  "case-number": "Case Number",
  "party-name": "Party Name",
  judge: "Judge",
};

/**
 * Results page for the NGT "Judgement / Orders" search. Reached from
 * <NGTJudgementSearch /> via navigate("/portal/ngt/judgementList", { state }).
 *
 * Expects location.state: { judgements:[{ sNo, bench, diaryNumber, caseNumber,
 *   partyName, dateOfOrder, status, pdfUrl }], searchType, searchParams }.
 */
function NGTJudgementList() {
  const location = useLocation();
  const navigate = useNavigate();

  const { judgements = [], searchType = "date", cookies = null } = location.state || {};

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowLoading, setRowLoading] = useState(null); // diaryNumber currently loading

  // Fetch detail for a clicked row, then open the details page.
  const handleRowClick = async (row) => {
    const filingNumber = row.diaryNumber;
    const zone = extractZone(row.caseNumber);

    if (!filingNumber || !zone) {
      toast.error("This row is missing the filing number or bench needed to open details.");
      return;
    }
    if (rowLoading) return;

    setRowLoading(filingNumber);
    try {
      const res = await getJudgementDetail(zone, filingNumber, cookies);
      if (res?.success === false || !res?.data) {
        toast.error(res?.error || "Failed to load case details.", { duration: 5000 });
        return;
      }
      navigate("/portal/ngt/judgementDetails", {
        state: { detail: res.data, row, zone, filingNumber, cookies },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Failed to load case details";
      toast.error(msg, { duration: 5000 });
    } finally {
      setRowLoading(null);
    }
  };

  // Client-side filter across visible fields
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return judgements;
    return judgements.filter((row) =>
      [row.caseNumber, row.diaryNumber, row.partyName, row.bench, row.status, row.dateOfOrder]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    );
  }, [judgements, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const pageRows = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);
  const firstRow = (page - 1) * ROWS_PER_PAGE;

  // Summary counts for the stat cards — derived from the status field already
  // present on each order record, no extra fetch.
  const statusCounts = useMemo(() => {
    const disposed = judgements.filter(
      (r) => String(r.status || "").toUpperCase() === "DISPOSED"
    ).length;
    return { total: judgements.length, disposed, pending: judgements.length - disposed };
  }, [judgements]);

  // Page-number window for the pagination control
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, page - 2);
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    return pageNumbers;
  };

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  // No results in navigation state (e.g. page refresh / direct visit)
  if (!location.state) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex min-h-screen flex-col items-center justify-center px-4">
          <div className="mb-4"><FindCaseStepper current={3} /></div>
          <p className="mb-4 text-base font-medium text-slate-700">No search results to display.</p>
          <Link
            to="/portal/fetchfromngt"
            className="h-10 rounded-lg bg-yellow-500 px-6 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-yellow-400"
          >
            Back to Search
          </Link>
        </div>
      </div>
    );
  }

  const searchLabel = SEARCH_TYPE_LABELS[searchType] || searchType;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Stepper */}
        <div className="mb-4"><FindCaseStepper current={3} /></div>

        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="text-xl font-bold text-slate-900">NGT Judgements / Orders</h1>
        </div>

        {/* Summary stat cards */}
        {judgements.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
            {[
              { label: "Total Found", value: statusCounts.total, Icon: Users, tint: "bg-indigo-50 text-indigo-500" },
              { label: "Pending", value: statusCounts.pending, Icon: Clock, tint: "bg-green-50 text-green-500" },
              { label: "Disposed", value: statusCounts.disposed, Icon: CircleCheck, tint: "bg-red-50 text-red-500" },
            ].map(({ label, value, Icon, tint }) => (
              <div
                key={label}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold leading-tight text-slate-900">{value}</p>
                  <p className="truncate text-xs text-slate-500">{label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Orders Table Card */}
        <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Toolbar — search + info */}
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search in results..."
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                  title="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <span className="text-sm text-slate-500">Searched by {searchLabel}</span>
          </div>

          {filtered.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-base font-medium text-slate-700">
                {searchQuery ? "No results match your search" : "No orders found"}
              </p>
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">S.No</th>
                    <th className="px-4 py-3">Zonal Bench</th>
                    <th className="px-4 py-3">Diary Number</th>
                    <th className="px-4 py-3">Case Number</th>
                    <th className="px-4 py-3">Party Name</th>
                    <th className="px-4 py-3 whitespace-nowrap">Order Date</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="w-16 px-4 py-3 text-center">Order</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, idx) => {
                    const isRowLoading = rowLoading === row.diaryNumber;
                    const openRow = () => handleRowClick(row);
                    const cellClass = "px-4 py-3.5 text-sm text-slate-600 cursor-pointer";
                    return (
                      <tr
                        key={`${row.diaryNumber || idx}-${idx}`}
                        className={`border-b border-slate-100 transition-colors duration-150 hover:bg-slate-50 ${
                          isRowLoading ? "opacity-60" : ""
                        }`}
                        title="Click to view case details"
                      >
                        <td className={cellClass} onClick={openRow}>{row.sNo || firstRow + idx + 1}</td>
                        <td className={cellClass} onClick={openRow}>{row.bench || "—"}</td>
                        <td className={cellClass} onClick={openRow}>{row.diaryNumber || "—"}</td>
                        <td
                          className="px-4 py-3.5 text-sm font-medium text-slate-900 cursor-pointer"
                          onClick={openRow}
                        >
                          <span className="inline-flex items-center gap-2">
                            {isRowLoading && <Loader2 className="h-4 w-4 animate-spin text-yellow-500" />}
                            {row.caseNumber || "—"}
                          </span>
                        </td>
                        <td className={cellClass} onClick={openRow}>{row.partyName || "—"}</td>
                        <td className={`${cellClass} whitespace-nowrap`} onClick={openRow}>
                          {row.dateOfOrder || "—"}
                        </td>
                        <td className={cellClass} onClick={openRow}>
                          <span className={`${STATUS_PILL_BASE} ${statusPillSoft(row.status)}`}>
                            {row.status || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          {row.pdfUrl ? (
                            <a
                              href={row.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title="Download order PDF"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer — result range + pagination */}
          {filtered.length > 0 && (
            <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
              <p className="text-sm text-slate-500">
                Showing {firstRow + 1} to {Math.min(page * ROWS_PER_PAGE, filtered.length)} of{" "}
                {filtered.length} results
              </p>

              {totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {page > 3 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                      >
                        1
                      </button>
                      {page > 4 && <span className="px-1 text-slate-400">...</span>}
                    </>
                  )}

                  {getPageNumbers().map((number) => (
                    <button
                      key={number}
                      type="button"
                      onClick={() => setCurrentPage(number)}
                      className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                        page === number
                          ? "bg-yellow-500 text-slate-900"
                          : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {number}
                    </button>
                  ))}

                  {page < totalPages - 2 && (
                    <>
                      {page < totalPages - 3 && <span className="px-1 text-slate-400">...</span>}
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NGTJudgementList;
